import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import type { AgendaPublic, Horaires } from '@iobiz/shared'
import { ApiError } from '../../api/client.js'
import { ChampDate } from '../../ui/ChampDate.js'
import { agendaVitrine, poserRdvVitrine, type ArticlePublic } from './api.js'

/**
 * La prise de rendez-vous en ligne, en MODALE.
 *
 * ⭐ La même grille que l'agenda de la boutique : une colonne par place, une
 * ligne par créneau, un « + » là où c'est libre. Le visiteur choisit son heure
 * d'un coup d'œil au lieu de parcourir une liste de boutons — et il voit du
 * même coup si la boutique est chargée ce jour-là.
 *
 * ⚠️ Une case occupée est VIDE et grise. Ni nom, ni prestation, ni libellé de
 * poste : un agenda public bavard livrerait la clientèle d'un commerçant à son
 * voisin. Les colonnes ne sont que des numéros.
 */
function civile(d: Date): string {
  // ⚠️ Composantes LOCALES : `toISOString()` recule d'un jour en soirée à
  // l'est de Greenwich, et le visiteur se verrait proposer la veille.
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const jj = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${jj}`
}

/**
 * Le prochain jour OUVERT, et non aujourd'hui.
 *
 * ⭐ Un visiteur qui arrive un dimanche ouvrait la modale sur une grille vide
 * et « la boutique est fermée » — il devait deviner qu'il fallait changer la
 * date pour voir quoi que ce soit. On lui présente directement le premier jour
 * où il peut venir.
 */
function premierJourOuvert(horaires: Horaires | undefined): string {
  const d = new Date()
  d.setHours(12, 0, 0, 0)
  if (!horaires) return civile(d)
  for (let i = 0; i < 14; i++) {
    const journee = horaires.days[d.getDay()]
    if (journee && !journee.closed && journee.ranges.length > 0) return civile(d)
    d.setDate(d.getDate() + 1)
  }
  return civile(new Date())
}

/**
 * Le jour OUVERT qui suit une date civile — pour passer outre un jour complet
 * ou déjà terminé.
 */
function jourOuvertSuivant(horaires: Horaires | undefined, depuis: string): string {
  const d = new Date(`${depuis}T12:00:00`)
  for (let i = 0; i < 14; i++) {
    d.setDate(d.getDate() + 1)
    const journee = horaires?.days[d.getDay()]
    if (!horaires || (journee && !journee.closed && journee.ranges.length > 0)) return civile(d)
  }
  return civile(d)
}

/** Nombre maximal de jours sautés d'office : deux semaines d'agenda. */
const SAUTS_MAX = 14

export function ModaleRdvEnLigne({
  slug,
  prestations,
  prestationInitiale,
  horaires,
  couleur,
  onClose,
}: {
  slug: string
  prestations: ArticlePublic[]
  /** La prestation cliquée, quand on vient d'une carte. */
  prestationInitiale?: string | undefined
  /** Les horaires de la boutique — pour ouvrir sur un jour où elle reçoit. */
  horaires?: Horaires | undefined
  couleur: string
  onClose: () => void
}): JSX.Element {
  const reservables = prestations.filter((p) => p.dureeMinutes)

  const [itemId, setItemId] = useState(prestationInitiale || (reservables[0]?.id ?? ''))
  const [jour, setJour] = useState(() => premierJourOuvert(horaires))
  /**
   * ⚠️ La date a-t-elle été choisie par L'APPLICATION ?
   *
   * Tant que oui, un jour sans aucune place libre est SAUTÉ : ouverte à 19 h
   * sur une boutique qui ferme à 18 h, la modale affichait une grille vide sur
   * aujourd'hui, et le visiteur devait deviner qu'il fallait changer la date.
   * Le serveur, qui juge « passé » à l'horloge de la BOUTIQUE, dit mieux que le
   * navigateur ce qui reste — c'est donc sa réponse qui décide.
   *
   * Dès que le visiteur choisit lui-même un jour, on le respecte, complet ou
   * non : il sait ce qu'il cherche.
   */
  const [jourAuto, setJourAuto] = useState(true)
  const sauts = useRef(0)
  const [jourSaute, setJourSaute] = useState(false)
  const [agenda, setAgenda] = useState<AgendaPublic | null>(null)
  /**
   * La case retenue : une heure ET une place.
   *
   * ⚠️ Le choix ne portait que sur l'heure : cliquer « Poste 1 » surlignait
   * toute la ligne, les deux places d'un coup, et le visiteur ne pouvait pas
   * désigner celle qu'il voulait. Une grille où l'on clique une case doit
   * retenir cette case-là.
   */
  const [choisi, setChoisi] = useState<{ debut: string; poste: number } | null>(null)
  const [reference, setReference] = useState('')
  const [nom, setNom] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [confirme, setConfirme] = useState<string | null>(null)

  useEffect(() => {
    if (!itemId) return
    let vivant = true
    setAgenda(null)
    setChoisi(null)
    void agendaVitrine(slug, itemId, jour)
      .then((r) => {
        if (!vivant) return
        const aucunePlace = r.creneaux.every((c) => !c.places.some(Boolean))
        if (jourAuto && aucunePlace && sauts.current < SAUTS_MAX) {
          sauts.current += 1
          setJourSaute(true)
          setJour(jourOuvertSuivant(horaires, jour))
          return
        }
        setAgenda(r)
      })
      .catch(() => vivant && setAgenda({ postes: 0, creneaux: [] }))
    return () => {
      vivant = false
    }
    // `jourAuto` et `horaires` ne relancent pas la recherche : seuls le jour et
    // la prestation la changent.
  }, [slug, itemId, jour])

  const heure = (iso: string): string =>
    new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

  async function reserver(): Promise<void> {
    if (!choisi) return
    setEnvoi(true)
    setErreur(null)
    try {
      const r = await poserRdvVitrine(slug, {
        reference: reference.trim(),
        nom: nom.trim(),
        itemId,
        startsAt: choisi.debut,
        posteIndex: choisi.poste,
      })
      setConfirme(
        `C’est noté : le ${new Date(r.debut).toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })} à ${heure(r.debut)}. La boutique vous confirmera.`,
      )
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Réservation impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  const colonnes = Math.max(1, agenda?.postes ?? 1)

  /**
   * ⚠️ PLEIN ÉCRAN sur téléphone.
   *
   * En fenêtre, la modale ne faisait que 311 px sur un écran de 375 : soixante
   * pixels partaient en marges, et la grille — colonne d'heures plus une par
   * poste — devait déjà défiler horizontalement pour deux places. Sur l'écran
   * où ces pages se lisent le plus, c'est la place qui manque le moins.
   */
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('sm'))

  /** La grille se resserre sur téléphone : deux postes doivent tenir sans défiler. */
  const grille = mobile
    ? `54px repeat(${colonnes}, minmax(56px, 1fr))`
    : `72px repeat(${colonnes}, minmax(96px, 1fr))`

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth fullScreen={mobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        Prendre rendez-vous
        <Box sx={{ flex: 1 }} />
        <IconButton aria-label="Fermer" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 3, sm: 6 } }}>
        {confirme ? (
          <Alert severity="success" sx={{ fontSize: '1rem' }}>
            {confirme}
          </Alert>
        ) : reservables.length === 0 ? (
          <Alert severity="info">
            Aucune prestation n’est réservable en ligne pour le moment. Appelez-nous, nous
            trouverons un créneau.
          </Alert>
        ) : (
          <Box sx={{ display: 'grid', gap: 4 }}>
            <Box
              sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' } }}
            >
              <TextField
                select
                label="Prestation"
                value={itemId}
                onChange={(e) => setItemId(e.target.value)}
              >
                {reservables.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nom} — {p.dureeMinutes} min
                  </MenuItem>
                ))}
              </TextField>
              {/* ⭐ Le calendrier de l'APPLICATION, comme la date de naissance
                  d'une fiche client. Le sélecteur natif change d'apparence et
                  de langue d'un téléphone à l'autre, et n'a aucun moyen de dire
                  quels jours la boutique reçoit.
                  ⚠️ Les jours de FERMETURE sont grisés : un dimanche cliquable
                  qui ne propose ensuite aucun créneau laisse croire à une
                  panne. Et jamais dans le passé — on ne prend pas rendez-vous
                  hier. */}
              <ChampDate
                label="Jour"
                value={jour}
                onChange={(v) => {
                  setJourAuto(false)
                  setJourSaute(false)
                  setJour(v)
                }}
                min="today"
                jourFerme={(d) => {
                  const journee = horaires?.days[d.getDay()]
                  return (
                    Boolean(horaires) && (!journee || journee.closed || journee.ranges.length === 0)
                  )
                }}
              />
            </Box>

            {agenda === null && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Recherche des disponibilités…
              </Typography>
            )}

            {/* Le saut se DIT : sans cela, le visiteur croit qu'on lui impose
                une date au hasard. */}
            {agenda && jourAuto && jourSaute && (
              <Alert severity="info">
                Plus de place disponible avant cette date : voici le premier jour où la boutique
                peut vous recevoir.
              </Alert>
            )}

            {agenda?.creneaux.length === 0 && (
              <Alert severity="info">
                Rien ce jour-là — la boutique est fermée ou complète. Essayez une autre date.
              </Alert>
            )}

            {agenda && agenda.creneaux.length > 0 && (
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 'fit-content' }}>
                  {/* Entête : les places, numérotées. */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: grille,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Box sx={{ px: 2, py: 1 }} />
                    {/* « Poste 1 », et non « 1 » : un chiffre seul en tête de
                        colonne ne dit pas de quoi il est le numéro. */}
                    {Array.from({ length: colonnes }, (_, i) => (
                      <Box key={i} sx={{ px: 2, py: 1, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Poste {i + 1}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {agenda.creneaux.map((c, ligne) => (
                    <Box
                      key={c.debut}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: grille,
                        borderTop: ligne === 0 ? 0 : 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          color: 'text.secondary',
                          fontVariantNumeric: 'tabular-nums',
                          borderRight: 1,
                          borderColor: 'divider',
                        }}
                      >
                        {heure(c.debut)}
                      </Box>

                      {c.places.map((libre, i) => {
                        const retenue = choisi?.debut === c.debut && choisi.poste === i
                        return (
                          <Box
                            key={i}
                            onClick={() => libre && setChoisi({ debut: c.debut, poste: i })}
                            sx={{
                              // Centré dans les DEUX sens : la case est la
                              // cible, le « + » doit en marquer le milieu.
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 44,
                              borderLeft: 1,
                              borderColor: 'divider',
                              cursor: libre ? 'pointer' : 'default',
                              // ⚠️ Occupé = gris et VIDE. Pas d'étiquette, pas
                              // de croix : rien qui laisse deviner ce qu'il y a
                              // derrière.
                              bgcolor: libre
                                ? retenue
                                  ? couleur
                                  : 'transparent'
                                : 'action.disabledBackground',
                              '&:hover':
                                libre && !retenue ? { bgcolor: 'action.hover' } : undefined,
                            }}
                          >
                            {libre && (
                              <Box
                                component="span"
                                role="img"
                                aria-label={`Réserver à ${heure(c.debut)} — poste ${i + 1}`}
                                sx={{
                                  fontSize: 14,
                                  lineHeight: 1,
                                  color: retenue ? '#fff' : 'text.primary',
                                }}
                              >
                                +
                              </Box>
                            )}
                          </Box>
                        )
                      })}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {choisi && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Rendez-vous à {heure(choisi.debut)}, poste {choisi.poste + 1} — indiquez-nous qui
                  vous êtes
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  }}
                >
                  <TextField
                    label="Votre référence client"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    helperText="Sur votre carte de fidélité ou votre ticket."
                  />
                  <TextField
                    label="Votre nom"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    helperText="Le nom sous lequel vous êtes enregistré."
                  />
                </Box>

                {erreur && (
                  <Alert severity="error" role="alert">
                    {erreur}
                  </Alert>
                )}

                <Box>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={envoi || reference.trim() === '' || nom.trim() === ''}
                    onClick={() => void reserver()}
                    sx={{ bgcolor: couleur, '&:hover': { bgcolor: couleur } }}
                  >
                    Confirmer le rendez-vous de {heure(choisi.debut)}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
